import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

export function PharmacyRejectionBanner() {
    const [items, setItems] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(() => {
        api.get("/institution/pharmacy-rejections").then((r) => setItems(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 90000);
        return () => clearInterval(t);
    }, [load]);

    const ack = (e, id) => {
        e.stopPropagation();
        api.post(`/institution/pharmacy-rejections/${id}/ack`).then(load).catch(() => {});
    };

    const openRecord = (o) => {
        navigate(`/institution/care-management?q=${encodeURIComponent(o.inmate_number || o.inmate_name || "")}`);
    };

    if (items.length === 0) return null;

    return (
        <div data-testid="pharmacy-rejection-banner" className="bg-red-50 border-b border-red-200">
            <button
                onClick={() => setExpanded((v) => !v)}
                className="w-full px-6 py-3 flex items-center gap-3 text-left"
                data-testid="pharmacy-rejection-banner-toggle"
            >
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                <div className="text-sm text-red-900 font-medium">
                    <span className="font-bold">{items.length}</span> prescription{items.length === 1 ? "" : "s"} rejected by the pharmacy — needs follow-up. The prescriber has been alerted; verify insurance/routing.
                </div>
                <span className="ml-auto text-red-700 shrink-0">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
            </button>
            {expanded && (
                <div className="px-6 pb-3 space-y-1.5">
                    {items.map((o) => (
                        <div key={o.id} data-testid={`pharmacy-rejection-item-${o.id}`}
                            className="flex flex-wrap items-center gap-2 bg-white border border-red-200 rounded-lg px-3 py-2 text-sm">
                            <span className="font-semibold text-phoenix-navy">{o.inmate_name || o.inmate_number}</span>
                            {o.inmate_number && <span className="font-mono text-[11px] text-muted-foreground">#{o.inmate_number}</span>}
                            <span className="text-phoenix-navy">{o.medication}</span>
                            <span className="text-xs text-red-700">— {o.reason}</span>
                            <span className="ml-auto flex gap-1.5">
                                <button data-testid={`pharmacy-rejection-open-${o.id}`} onClick={() => openRecord(o)}
                                    className="px-2.5 py-1 text-[11px] font-semibold rounded-md bg-phoenix-navy text-white hover:bg-phoenix-navy-2 transition-colors">
                                    Open record
                                </button>
                                <button data-testid={`pharmacy-rejection-dismiss-${o.id}`} onClick={(e) => ack(e, o.id)}
                                    title="Dismiss (I've followed up)"
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-secondary text-muted-foreground hover:bg-secondary/70 transition-colors">
                                    <X className="w-3 h-3" /> Dismiss
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
